import React, { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useDeepCompareEffect } from 'react-use'

import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'

import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'

import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import { Appointment, Options, Requisition } from 'models'

import FinalizeTaskDialog from '../FinalizeTaskController/FinalizeTaskDialog'

import styles from '../Dialog.module.sass'
import useSubmitTask from 'hooks/useSubmitTask'
import {
	ApproveMedicalActsClinicalSchema,
	ExtendedMedicalActsClinicalApprovalSchema,
} from './MedicalActsClinicalApprovalSchema'
import { MedicalActsClinicalApprovalSchema } from '../MedicalActsClinicalApprovalTaskController/MedicalActsClinicalApprovalSchema'
import { MedicalActsClinicalApprovalForm } from './MedicalActsClinicalApprovalForm'
import useSession from 'hooks/useSession'
import { Laterality, RequisitionType, SuggestType } from 'utils/enums'
import TaskMenuController from '../TaskMenu/TaskMenuController'
import TaskHeaderController from 'controllers/TaskManager/TaskSubControllers/TaskHeader/TaskHeader'
import TaskNotesController from 'controllers/TaskManager/TaskSubControllers/TaskNotesController/TaskNotesController'
import AlertBox from 'components/AlertBox/AlertBox'
import InfoList from 'components/InfoList/InfoList'
import { mapMedicalActsDetails } from 'controllers/TaskManager/TaskSubControllers/TaskDetailsControllers/detailsUtils'
import { useNoWaitFetchList } from 'store'
import { getNextAppointment } from '../utils'
import TaskRefusalController from 'controllers/TaskManager/TaskSubControllers/TaskRefusalController/TaskRefusalController'
import PersonalAccidentWarning from 'controllers/TaskManager/TaskSubControllers/PersonalAccidentWarning'
import { API_URL } from 'store/fetchUtils'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const buildPhysiotherapyLogBody = (scriptInfo, formData) => {
	const bodyWithoutRequest = {
		processId: scriptInfo.processId,
		requestingProviderId: scriptInfo.prestador_requerente,
		requisitionId: scriptInfo.requisitionIds,
		codes: Array.isArray(scriptInfo.medicalActId)
			? scriptInfo.medicalActId.join(',')
			: String(scriptInfo.medicalActId),
		postalCode: scriptInfo.postalCode7,
		providers:
			scriptInfo.response?.providerReferrals?.map((p) => ({
				providerId: p.providerId,
				profitProvider: Math.round(p.profit || 0),
				distance: p.distance || 0,
				speed: p.averageTime || 0,
			})) || [],
		chosenProvider: formData?.providerId || null,
		username: scriptInfo.username,
		justification: scriptInfo.response?.description || 'Aprovação manual',
		userJustification:
			scriptInfo.userJustification || 'Sugestao do modelo.',
		userJustificationId: scriptInfo.userJustificationId || null,
		suggestType: SuggestType.Physiotherapy,
	}
	return {
		...bodyWithoutRequest,
		request: JSON.stringify(bodyWithoutRequest),
	}
}

const buildImagingLogBody = (scriptInfo, formData) => {
	const bodyWithoutRequest = {
		processId: scriptInfo.processId,
		requestingProviderId: scriptInfo.prestador_requerente,
		requisitionId: scriptInfo.requisitionId,
		codes: Array.isArray(scriptInfo.select_acto)
			? scriptInfo.select_acto.join(',')
			: String(scriptInfo.select_acto),
		postalCode: scriptInfo.codpost7,
		scheduleDate: scriptInfo.data_marcacao
			? scriptInfo.data_marcacao + 'T00:00:00'
			: null,
		appointmentDate: scriptInfo.data_consulta
			? scriptInfo.data_consulta + 'T00:00:00'
			: null,
		providers:
			scriptInfo.response?.providerReferrals?.map((p) => ({
				providerId: p.providerId,
				profitProvider: Math.round(p.profit || 0),
				distance: p.distance || 0,
				speed: p.averageTime || 0,
			})) || [],
		chosenProvider: formData?.providerId || null,
		username: scriptInfo.username,
		justification: scriptInfo.response?.description || 'Aprovação manual',
		userJustification:
			scriptInfo.userJustification || 'Sugestao do modelo.',
		userJustificationId: scriptInfo.userJustificationId || null,
		suggestType: SuggestType.Imaging,
	}
	return {
		...bodyWithoutRequest,
		request: JSON.stringify(bodyWithoutRequest),
	}
}

const saveLogs = async (requisitionType, scriptInfo, formData) => {
	const isPhysiotherapy = requisitionType.id === RequisitionType.Physiotherapy
	const hasImagingData =
		(scriptInfo?.processId ?? '') !== '' ||
		(scriptInfo?.requisitionId ?? '') !== ''

	if (!isPhysiotherapy && !hasImagingData) return

	const logRequestBody = isPhysiotherapy
		? buildPhysiotherapyLogBody(scriptInfo, formData)
		: buildImagingLogBody(scriptInfo, formData)

	await fetch(`${API_URL}/api/suggest/logs`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(logRequestBody),
	})
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const MedicalActsClinicalApprovalTaskController = (props) => {
	const { i18n } = useGlobal()
	const { data, resolve, dismiss, id } = props
	const { requisitionType } = data
	const [defaultValues, setDefaultValues] = useState(null)
	const [argValues, setArgValues] = useState(null)
	const [submissionType, setSubmissionType] = useState(null)
	const history = useHistory()
	const { user: sessionUser } = useSession()
	const [scriptInfo, setScriptInfo] = useState({})
	const [resolverDisabled, setResolverDisabled] = useState(false)
	const validateRef = useRef(() => true)

	const IS_ADMINISTRATIVE = sessionUser?.roles.includes('manager')
	const IS_PROCESS_IN_ANALYSIS = data.processInjuredStateId === 2

	const schema = IS_ADMINISTRATIVE
		? submissionType === 'approve'
			? ApproveMedicalActsClinicalSchema({ ...argValues })
			: ExtendedMedicalActsClinicalApprovalSchema({ ...argValues })
		: MedicalActsClinicalApprovalSchema({ ...argValues })

	const methods = useForm({
		mode: 'onSubmit',
		resolver: zodResolver(schema),
	})
	const { handleSubmit, formState, watch, reset, getValues } = methods

	const {
		error,
		submitting,
		resolveTask,
		approveTask,
		requestApprovalTask,
		requestManagerApprovalTask,
	} = useSubmitTask(data?.id, formState?.errors)

	const [outsideNetwork] = watch(['outsideNetwork'])

	const { contents } = useNoWaitFetchList(
		[
			`clinical_processes/${data?.processId}/appointments`,
			`clinical_processes/${data?.processId}/requisitions/${data?.requisitionId}/notes`,
			`clinical_processes/${data.processId}/requisitions/${data.requisitionId}`,
			outsideNetwork && `metadata/districts`,
			!outsideNetwork
				? argValues?.specialtyId
					? `clinical_processes/${data.processId}/requisitions/${data.requisitionId}/specialties/${argValues.specialtyId}/service_providers`
					: `clinical_processes/${data.processId}/requisitions/${data.requisitionId}/service_providers`
				: null,
			`metadata/valid_establishments/${
				data?.insuranceCompany?.id
			}/${encodeURIComponent(data?.admissionDate?.UTC)}/${
				data?.segment?.id
			}`,
			`clinical_processes/${data?.processId}`,
		],
		[
			Appointment.fromJSON,
			(json) => json.notes,
			Requisition.fromJSON,
			IS_ADMINISTRATIVE && outsideNetwork && Options.fromSimpleObjectJSON,
			IS_ADMINISTRATIVE && argValues && !outsideNetwork
				? Options.fromSimpleObjectJSON
				: null,
			(json) => ({
				id: `${json?.idEstabelecimento}`,
				text: json?.nomeEstabelecimento,
			}),
		]
	)

	const [
		appointmentsContents,
		notesContents,
		requisitionContents,
		districtsContents,
		serviceProvidersContents,
		establishmentsContents,
		processContents,
	] = contents

	const { contents: appointments } =
		appointmentsContents && appointmentsContents.state === 'hasValue'
			? appointmentsContents
			: { state: appointmentsContents?.state }

	const { contents: notes, state: notesState } =
		notesContents && notesContents.state === 'hasValue'
			? notesContents
			: { state: notesContents?.state }

	const { contents: requisition, state: requisitionState } =
		requisitionContents && requisitionContents.state === 'hasValue'
			? requisitionContents
			: { state: requisitionContents?.state }

	const { contents: districts } =
		districtsContents && districtsContents.state === 'hasValue'
			? districtsContents
			: { state: districtsContents?.state }

	const { contents: serviceProviders } =
		serviceProvidersContents &&
		serviceProvidersContents.state === 'hasValue'
			? serviceProvidersContents
			: { state: serviceProvidersContents?.state }

	const { contents: establishments } =
		establishmentsContents && establishmentsContents.state === 'hasValue'
			? establishmentsContents
			: { state: establishmentsContents?.state }

	const { contents: process } =
		processContents && processContents.state === 'hasValue'
			? processContents
			: { state: processContents?.state }

	const isLoading = requisitionState === 'loading'
	const isNotesLoading = notesState === 'loading'

	const IS_DENTAL_MEDICINE = requisition?.type?.id === 8

	useEffect(() => {
		if (
			!isLoading &&
			(data.formState || defaultValues) &&
			!formState.isDirty &&
			formState.submitCount === 0
		) {
			const result = schema.safeParse(data.formState)
			reset &&
				reset(
					data.formState && result.success
						? data.formState
						: defaultValues
				)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		reset,
		defaultValues,
		isLoading,
		data.formState,
		formState.isDirty,
		formState.submitCount,
	])

	useDeepCompareEffect(() => {
		if (!isLoading) {
			let defaultValues = {}

			if (requisition) {
				const requestedValues = (requisition?.medicalActs || []).map(
					(item) => {
						const itemRequestedQuantity =
							item?.approvedQuantity ||
							item?.requestedQuantity ||
							1

						return {
							id: item.id,
							value: itemRequestedQuantity,
							checked: item.refused
								? false
								: itemRequestedQuantity > 0,
							disabled: IS_ADMINISTRATIVE && item?.refused,
							laterality:
								item.laterality || Laterality.NotApplicable,
						}
					}
				)

				defaultValues = {
					...defaultValues,
					medicalActs: requestedValues,
				}

				if (
					requisition.providerDescription &&
					requisition.district?.id &&
					outsideNetwork
				) {
					defaultValues = {
						...defaultValues,
						'externalProvider.name':
							requisition.providerDescription,
						'externalProvider.districtId': requisition.district?.id,
					}
				}

				setArgValues({
					requestEscort: requisition?.requestEscort,
					requiresChargeAmount: data?.requiresChargeAmount,
					specialtyId: requisition?.specialty?.id,
				})
			}

			if (requisition?.requestEscort && IS_ADMINISTRATIVE) {
				defaultValues = {
					...defaultValues,
					'needsOptions.housing': false,
					'needsOptions.transport': false,
				}
			}

			if (IS_ADMINISTRATIVE) {
				defaultValues = {
					...defaultValues,
					notes: requisition?.notes || '',
					outsideNetwork: false,
					administrativeObservation:
						requisition?.administrativeObservation || '',
				}

				if (serviceProviders) {
					const establishmentId =
						requisition?.establishment?.id.toString()
					const hasEstablishment = serviceProviders.some(
						(provider) => provider.value === establishmentId
					)

					if (hasEstablishment) {
						defaultValues = {
							providerId: establishmentId,
							...defaultValues,
						}
					}
				}
			}

			defaultValues = { ...defaultValues, needEscort: false }

			setDefaultValues({ ...defaultValues })
		}
	}, [isLoading, requisition ?? {}, serviceProviders ?? []])

	const onSubmit = async (formData) => {
		if (!validateRef.current()) return

		let finalData = {}
		const {
			medicalActs,
			externalProvider,
			providerId,
			notes,
			...remainingData
		} = formData

		finalData = remainingData

		if (medicalActs && medicalActs.length) {
			const mappedMedicalActs = medicalActs.map(
				({ id, value, laterality }) => {
					if (value === 0) {
						const defaultValue = defaultValues.medicalActs.find(
							(ele) => ele.id === id
						)?.value
						return {
							id,
							quantity: defaultValue ?? 0,
							refuse: true,
							laterality,
						}
					}
					return { id, quantity: value, refuse: false, laterality }
				}
			)

			finalData = { medicalActs: mappedMedicalActs, ...finalData }
		}

		finalData = outsideNetwork
			? { ...finalData, externalProvider }
			: { ...finalData, providerId }

		if (submissionType === 'resolve') {
			resolveTask(finalData)
				.then(async () => {
					await resolve()
					try {
						await saveLogs(requisitionType, scriptInfo, formData)
					} catch (error) {
						console.error('Erro ao gravar logs:', error)
					}
				})
				.catch(() => {
					console.error(
						'Logs não enviados - Erro 404 ou task não encontrada'
					)
				})
		} else if (submissionType === 'approve') {
			approveTask(finalData)
				.then(async () => {
					await resolve()
					try {
						await saveLogs(requisitionType, scriptInfo, formData)
					} catch (error) {
						console.error('Erro ao gravar logs:', error)
					}
				})
				.catch(() => {
					console.error(
						'Logs não enviados - Erro 404 ou task não encontrada'
					)
				})
		}
	}

	if (isLoading && !formState.isDirty) {
		return <LoadingIndicator />
	}

	const customError = error?.map((err) => ({
		title: i18n`Error`,
		text: err,
	}))

	const nextAppointment = getNextAppointment(appointments)

	const medicalActs =
		requisitionType.id === RequisitionType.Imagiology
			? requisitionContents?.contents.medicalActs.map(
					(item) => item.medicalActId
			  )
			: requisitionContents?.contents.medicalActs.map(
					(item) => item.medicalActId
			  )

	const appointmentContent =
		appointmentsContents &&
		appointmentsContents.state === 'hasValue' &&
		appointmentsContents.contents.filter(
			(item) => item.id === data.appointmentId
		)

	const updateFormData = (data) => {
		setScriptInfo((prevData) => ({ ...prevData, ...data }))
	}

	return (
		<>
			{submitting && (
				<FinalizeTaskDialog
					dismiss={dismiss}
					submitting={submitting}
					task={data}
					submissionType={submissionType}
				/>
			)}
			<FormProvider {...methods}>
				<div className={styles['form-container']}>
					<TaskHeaderController
						task={data}
						requisition={requisition}
					/>
					<div className={styles['notes-form-container']}>
						<TaskNotesController
							data={{
								id: data.id,
								processId: data.processId,
								taskNote: notes,
							}}
							requisition={requisition}
							isNotesLoading={isNotesLoading}
						/>
					</div>

					<form
						id={`form_${id}`}
						className={styles['form']}
						onSubmit={handleSubmit(onSubmit)}
					>
						{customError && customError.length > 0 && (
							<AlertBox
								className={styles['alert-box']}
								type='error'
								items={customError}
							/>
						)}

						{IS_PROCESS_IN_ANALYSIS && IS_DENTAL_MEDICINE && (
							<AlertBox
								className={styles['alert-box']}
								type='warning'
								items={[
									{
										title: i18n`Warning`,
										text: i18n`The process is under review`,
									},
								]}
							/>
						)}

						<TaskRefusalController requisition={requisition} />

						<PersonalAccidentWarning task={data} />

						{IS_ADMINISTRATIVE && (
							<>
								<InfoList
									items={[
										{
											title: i18n`Medical observation`,
											text:
												requisition?.clinicalObservation ||
												'-',
											icon: 'observation',
										},
									]}
								/>
								<InfoList
									items={mapMedicalActsDetails(
										requisition,
										nextAppointment,
										i18n
									)}
								/>
							</>
						)}
						<MedicalActsClinicalApprovalForm
							data={{
								requisition,
								isAdministrative: IS_ADMINISTRATIVE,
								outsideNetwork: outsideNetwork,
								districts: districts,
								serviceProviders: serviceProviders,
								isFromIsland: data?.injured?.isFromIsland,
								requiresChargeAmount:
									data?.requiresChargeAmount,
								establishments: establishments,
								accidentType: data.segment?.id,
								postalCode: data?.injured?.postalCode,
								medicalActs: medicalActs,
								onUpdateFormData: updateFormData,
								onResolverDisabled: setResolverDisabled,
								appointmentContent: appointmentContent,
								processContent: process,
								validateRef,
							}}
						/>
					</form>
				</div>
			</FormProvider>
			{IS_ADMINISTRATIVE ? (
				<TaskMenuController
					data={{
						formId: id,
						options: ['postpone', 'forward', 'refuse'],
						submitting,
						setSubmissionType,
						submissionType: 'resolve',
						requestApprovalTask,
						requestManagerApprovalTask,
						getValues,
						disableResolver: false,
					}}
				/>
			) : (
				<TaskMenuController
					data={{
						formId: id,
						options: ['postpone', 'forward', 'refuse'],
						history,
						submitting,
						setSubmissionType,
						submissionType: 'approve',
						getValues,
						disableResolver: false,
					}}
				/>
			)}
		</>
	)
}

export default MedicalActsClinicalApprovalTaskController
